import { Extension } from "@tiptap/core";
import type { Mark } from "@tiptap/pm/model";
import type { Selection } from "@tiptap/pm/state";

type CustomStateStorage = {
  openLinkDialog: boolean;
  savedSelection: Selection | null;
  textSelected: string;
  existingLink: string;
  openBubbleLink: boolean;
  // Callbacks for state changes to notify React components
  onStateChange?: () => void;
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    customState: {
      resetForLinkDialog: () => ReturnType;
      toggleLinkDialog: (value?: boolean) => ReturnType;
      toggleBubbleLink: (value?: boolean) => ReturnType;
    };
  }
  interface Storage {
    customState: CustomStateStorage;
  }
}

export const CustomState = Extension.create({
  name: "customState",

  addStorage(): CustomStateStorage {
    return {
      openLinkDialog: false,
      savedSelection: null,
      textSelected: "",
      existingLink: "",
      openBubbleLink: false,
      onStateChange: undefined,
    };
  },

  addCommands() {
    return {
      resetForLinkDialog:
        () =>
        ({ editor }) => {
          editor.storage.customState.textSelected = "";
          editor.storage.customState.existingLink = "";
          editor.storage.customState.savedSelection = null;
          editor.storage.customState.onStateChange?.();
          return true;
        },
      toggleLinkDialog:
        (value?: boolean) =>
        ({ editor }) => {
          const state = editor.state;
          const { selection, doc } = state;
          editor.storage.customState.openLinkDialog =
            value ?? !editor.storage.customState.openLinkDialog;
          let selectedText = "";
          let linkMark = "";

          if (!selection.empty) {
            selectedText = state.doc.textBetween(selection.from, selection.to);
            linkMark = editor.isActive("link")
              ? editor.getAttributes("link").href
              : "";
          }

          if (editor.isActive("link")) {
            const pos = selection.from;
            const $pos = doc.resolve(pos);
            const parent = $pos.parent;

            let offset = 0;

            for (let i = 0; i < parent.childCount; i++) {
              const child = parent.child(i);
              const childStart = $pos.start() + offset;
              const childEnd = childStart + child.nodeSize;

              const hasLink = child.marks.find(
                (mark: Mark) => mark.type.name === "link",
              );

              if (hasLink && pos >= childStart && pos <= childEnd) {
                selectedText = child.text || "";
                linkMark =
                  child.marks.find((mark: Mark) => mark.type.name === "link")
                    ?.attrs?.href || "";
                break;
              }

              offset += child.nodeSize;
            }
          }

          editor.storage.customState.textSelected = selectedText;
          editor.storage.customState.existingLink = linkMark;
          editor.storage.customState.savedSelection = selection;

          // Notify React components of state change
          editor.storage.customState.onStateChange?.();

          return true;
        },
      toggleBubbleLink:
        (value?: boolean) =>
        ({ editor }) => {
          const linkMark = editor.isActive("link")
            ? editor.getAttributes("link").href
            : "";
          editor.storage.customState.existingLink = linkMark;
          editor.storage.customState.openBubbleLink =
            value ?? !editor.storage.customState.openBubbleLink;

          // Notify React components of state change
          editor.storage.customState.onStateChange?.();

          return true;
        },
    };
  },
});
